import type { DisciplineOrderPayload, DisciplineSubscriptionPayload } from '../model/interfaces'
import type { AppRequestRoute } from '@shared/config'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplinePaymentGenerateOptions<TPayload> {
  disciplineId: string | number
  payload: TPayload
  onStart?: () => void
  onFinish?: () => void
}

/** `order_id` is the order to send; for installments it is the FIRST order, never the subscription. */
interface DisciplinePaymentGenerateResult {
  order_id: number
  link: string
  subscription_id?: number
}

/** A billing write that is NOT idempotent: the caller keeps Generate disabled while it runs and never retries it. */
const generate = async <TPayload>(
  routeName: AppRequestRoute,
  options: DisciplinePaymentGenerateOptions<TPayload>,
): Promise<DisciplinePaymentGenerateResult | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes(routeName, { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)

    toast.success('Payment order was generated')

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `POST /admin-api/disciplines/{discipline}/orders` — a one-time order and its checkout link. */
export const useDisciplineOrderStoreApi = (
  options: DisciplinePaymentGenerateOptions<DisciplineOrderPayload>,
): Promise<DisciplinePaymentGenerateResult | null> => generate('admin.disciplines.api.orders', options)

/** `POST /admin-api/disciplines/{discipline}/subscriptions` — an installment subscription and its first order's link. */
export const useDisciplineSubscriptionStoreApi = (
  options: DisciplinePaymentGenerateOptions<DisciplineSubscriptionPayload>,
): Promise<DisciplinePaymentGenerateResult | null> => generate('admin.disciplines.api.subscriptions', options)
